import React, { useState } from "react";
import "./ReportsPage.scss";
import Tabs from "../../Reuseable/Tabs/Tabs";
import RangePickerWithPresets from "../../Reuseable/RengePicker/RangePickerWithPresets";
import { BarChartOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import CustomFilterDropdown from "../../Reuseable/Allocation/CustomFilterDropdown/CustomFilterDropdown";
import { ReactComponent as DownloadIcon } from "../../assets/Icons/ReportDownload.svg";
import ReusableTable from "../../Reuseable/ReusableTable/ReusableTable";
import PanelCard from "../DashboardPage/components/PanelCard";

const EMPTY = "—";

const initialsOf = (name) =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

/* The metrics arrive pre-formatted as "315(74%)". Splitting the count from
   the share lets the count carry the weight and the share sit back as a
   chip, instead of both running together in one string. */
const METRIC = /^\s*(-?[\d.,]+)\s*\(\s*([\d.]+)\s*%\s*\)\s*$/;

const renderMetric = (text) => {
  const raw = String(text ?? "").trim();
  if (!raw) return <span className="reportNum is-zero">{EMPTY}</span>;

  const match = raw.match(METRIC);
  if (!match) {
    const isZero = Number(raw.replace(/,/g, "")) === 0;
    return (
      <span className={`reportNum${isZero ? " is-zero" : ""}`}>{raw}</span>
    );
  }

  const [, count, share] = match;
  const isZero = Number(count.replace(/,/g, "")) === 0;

  return (
    <span className="reportMetric">
      <span className={`reportMetric__num${isZero ? " is-zero" : ""}`}>
        {count}
      </span>
      <span className="reportMetric__pct">{share}%</span>
    </span>
  );
};

const ReportsPage = () => {
  const [selectedTab, setSelectedTab] = useState("Call Status Report");
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Generate dynamic sample data
  const dataSource = Array.from({ length: 40 }, (_, i) => {
    const firstName = `Guy Hawkins${i + 1}`;

    return {
      key: i,
      users: `${firstName}`,
      completed: `315(74%)`,
      cancelled: `0(0%)`,
      missed: `0`,
      rejected: "36(9%)",
      voicemail: `0`,
      answer: `71(17%)`,
      failed: `1(0%)`,
      agent: `0`,
      setup: `0`,
      massage: `0`,
      /* The IVR column shared "massage" as both its dataIndex and its key,
         so React saw two columns with the same key and both rendered the
         Welcome Message value. */
      ivr: `0`,
    };
  });

  const paginatedData = dataSource?.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const metricColumn = (title, dataIndex) => ({
    title,
    dataIndex,
    key: dataIndex,
    align: "right",
    render: renderMetric,
  });

  const columns = [
    {
      title: "Users",
      dataIndex: "users",
      key: "users",
      render: (text) => (
        <span className="reportUser">
          <span className="reportUser__avatar" aria-hidden="true">
            {initialsOf(text) || "NA"}
          </span>
          <span className="reportUser__name">{text || EMPTY}</span>
        </span>
      ),
    },
    metricColumn("Completed", "completed"),
    metricColumn("Cancelled", "cancelled"),
    metricColumn("Missed", "missed"),
    metricColumn("Rejected", "rejected"),
    metricColumn("Voicemail", "voicemail"),
    metricColumn("No Answer", "answer"),
    metricColumn("Failed", "failed"),
    metricColumn("No Answer by Agent", "agent"),
    metricColumn("Call not Setup", "setup"),
    metricColumn("Welcome Message", "massage"),
    metricColumn("IVR Message", "ivr"),
  ];

  /* No "All" entry: the filter chip carries a cross to clear the selection,
     so an explicit reset option is redundant. */
  const filters = [
    { label: "Supervisor", value: "f2" },
    { label: "Team Lead", value: "f3" },
    { label: "Telecaller", value: "f4" },
    { label: "Field Agent", value: "f5" },
  ];

  return (
    <div className="ReportsPage">
      <Tabs
        options={[
          { label: "Call Status Report", value: "Call Status Report" },
          { label: "Abandon Rate Report", value: "Abandon Rate Report" },
          { label: "Availability Report", value: "Availability Report" },
          { label: "Call Report", value: "Call Report" },
        ]}
        onChange={(val) => setSelectedTab(val)}
        active={selectedTab}
        selectorBreakpoint={644}
        classname="active-inactive-tab"
      />

      <PanelCard
        title={selectedTab}
        icon={<BarChartOutlined />}
        accent="brand"
        className="reportsTableCard"
        actions={
          <div className="calender-and-buttons">
            <RangePickerWithPresets />
            <div className="dropdown-and-icon">
              <div className="filter-dropdown">
                <CustomFilterDropdown buttonLabel="Filter" options={filters} />
              </div>
              {/* ReportDownload.svg is a whole 44x44 button graphic - a
                  gradient-filled rounded rect with its own stroke - so the
                  wrapper stays chrome-free and only adds the hit area, the
                  hover feedback and the accessible name. */}
              <Tooltip title="Download report">
                <button
                  type="button"
                  className="report-download"
                  aria-label="Download report"
                >
                  <DownloadIcon />
                </button>
              </Tooltip>
            </div>
          </div>
        }
      >
        <ReusableTable
          columns={columns}
          dataSource={paginatedData}
          rowKey="key"
          pageSize={pageSize}
          CurrentPageNo={currentPage}
          totalItems={dataSource.length}
          setPageSize={setPageSize}
          triggerChange={(page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          }}
        />
      </PanelCard>
    </div>
  );
};

export default ReportsPage;
